'use client';

/**
 * RailCard — discrete card primitive for the Insights surface.
 *
 * Bordered, titled, padded container. Sibling-stackable in a vertical
 * scroll. No internal max-height or overflow — let the parent (the
 * Insights surface) own the scroll. Cards stack inside the parent;
 * each is a self-contained unit.
 *
 * Phase 1 establishes the primitive. Future cards (Next Move,
 * Buyer Notes, Vendor cards) reuse this same component — only the
 * `title` and the body slot change.
 *
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { cn } from '@/lib/utils/cn';
import { text, surfaceBase } from '@/lib/design/tokens';
import type { LucideIcon } from 'lucide-react';

interface RailCardProps {
  title: string;
  icon?: LucideIcon;
  className?: string;
  /** Right-aligned header actions (refresh, pin, overflow menu, etc.). */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

export function RailCard({ title, icon: Icon, className, actions, children }: RailCardProps) {
  // Rail surface base — `text.*` em tokens in this card (and its children,
  // e.g. DocumentsSection) resolve against this. Rem-based legacy sections
  // are unaffected.
  return (
    <article
      style={{ fontSize: surfaceBase.rail }}
      className={cn(
        'rounded-md border border-border bg-card shadow-sm',
        className,
      )}
    >
      <header className="flex items-center gap-2 px-3 py-2 border-b border-border/60">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
        <h3 className={cn(text.heading, 'flex-1 min-w-0 truncate')}>
          {title}
        </h3>
        {actions && <div className="flex items-center gap-1 shrink-0">{actions}</div>}
      </header>
      <div className="px-3 py-3">
        {children}
      </div>
    </article>
  );
}
